import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  icdCodesService,
  CreateIcdCodeInput,
  UpdateIcdCodeInput,
} from "../../services/admin/icdcodes.service";

export function useIcdCodesAdmin() {
  return useQuery({
    queryKey: ["admin", "icd-codes"],
    queryFn: icdCodesService.getAll,
  });
}

export function useIcdCode(id?: string) {
  return useQuery({
    queryKey: ["admin", "icd-codes", id],
    queryFn: () => icdCodesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateIcdCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateIcdCodeInput) => icdCodesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "icd-codes"] });
      queryClient.invalidateQueries({ queryKey: ["icd-codes"] });
      notifications.show({
        title: "Success",
        message: "ICD Code created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create ICD Code",
        color: "red",
      });
    },
  });
}

export function useUpdateIcdCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateIcdCodeInput) => icdCodesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "icd-codes"] });
      queryClient.invalidateQueries({ queryKey: ["icd-codes"] });
      notifications.show({
        title: "Success",
        message: "ICD Code updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update ICD Code",
        color: "red",
      });
    },
  });
}

export function useDeleteIcdCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => icdCodesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "icd-codes"] });
      queryClient.invalidateQueries({ queryKey: ["icd-codes"] });
      notifications.show({
        title: "Success",
        message: "ICD Code deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete ICD Code",
        color: "red",
      });
    },
  });
}

export function useToggleIcdCodeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      icdCodesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "icd-codes"] });
      queryClient.invalidateQueries({ queryKey: ["icd-codes"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
